"use client"

import { m } from "framer-motion"
import Image from "next/image"
import { HOME_IMAGES } from "@/lib/home-images"

const PRODUCTS = [
  {
    id: 1,
    title: "iPhone 15 Pro Max",
    image: HOME_IMAGES.products.iphone15,
    category: "Smartphones",
  },
  {
    id: 2,
    title: "PlayStation 5",
    image: HOME_IMAGES.products.ps5,
    category: "Consoles",
  },
  {
    id: 3,
    title: "Gaming Headset Pro",
    image: HOME_IMAGES.products.headset,
    category: "Periféricos",
  },
  {
    id: 4,
    title: "DualSense Controller",
    image: HOME_IMAGES.products.dualsense,
    category: "Acessórios",
  },
  {
    id: 5,
    title: "Samsung Galaxy S24 Ultra",
    image: HOME_IMAGES.products.galaxy,
    category: "Smartphones",
  },
  {
    id: 6,
    title: "Mechanical Keyboard RGB",
    image: HOME_IMAGES.products.keyboard,
    category: "Periféricos",
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut" as const,
    },
  },
}

export function ShowcaseBento() {
  return (
    <section 
      id="products" 
      className="py-24 bg-gradient-to-b from-[#0F0F1A] to-[#050508] relative overflow-hidden"
      style={{ contain: "layout style paint" }}
    >
      <div className="absolute inset-0 opacity-15 pointer-events-none">
        <div 
          className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-[#00A3FF]/10 rounded-full blur-[120px] -translate-y-1/2"
          style={{ willChange: "opacity" }}
        />
        <div 
          className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-[#B829FF]/10 rounded-full blur-[120px] -translate-y-1/2"
          style={{ willChange: "opacity" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px", amount: 0.3 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{ willChange: "transform, opacity" }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-black font-[family-name:var(--font-orbitron)] text-white mb-4">
            Nossa Vitrine Premium
          </h2>
          <p className="text-slate-100 text-lg font-medium">
            Os melhores produtos de tecnologia e games para você.
          </p>
        </m.div>

        <m.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px", amount: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 md:grid-rows-3"
        >
          {PRODUCTS.map((product, index) => {
            const isLarge = index === 0
            const isMedium = index === 1

            return (
              <m.article
                key={product.id}
                variants={itemVariants}
                style={{ willChange: "transform, opacity" }}
                className={`group relative overflow-hidden rounded-2xl glass-cyber hover:border-[#B829FF]/60 transition-colors duration-500 ${
                  isLarge ? "md:col-span-2 md:row-span-2" : isMedium ? "md:col-span-2 md:row-span-1" : "md:col-span-1"
                }`}
              >
                <div className="relative overflow-hidden">
                  <div className={`relative w-full ${isLarge ? "h-[400px] md:h-[706px]" : isMedium ? "h-[300px]" : "h-[250px]"}`}>
                    <Image
                      src={product.image}
                      alt={product.title}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-700"
                      sizes={isLarge ? "(max-width: 768px) 100vw, 50vw" : isMedium ? "(max-width: 768px) 100vw, 50vw" : "(max-width: 768px) 100vw, 25vw"}
                      quality={isLarge ? 65 : 60}
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-[#050508]/60 to-transparent opacity-90" />
                </div>

                {/* Neon border glow on hover */}
                <div 
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none neon-box-purple"
                  style={{ willChange: "opacity" }}
                />

                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="inline-block px-4 py-1.5 bg-gradient-to-r from-[#B829FF]/30 to-[#00A3FF]/30 text-[#00F0FF] text-xs font-bold font-[family-name:var(--font-orbitron)] tracking-wider rounded-full mb-3 border border-[#00F0FF]/30">
                    {product.category}
                  </span>

                  <h3 className={`font-bold font-[family-name:var(--font-orbitron)] text-white ${isLarge ? "text-2xl md:text-3xl" : "text-xl"}`}>
                    {product.title}
                  </h3>
                </div>
              </m.article>
            )
          })}
        </m.div>
      </div>
    </section>
  )
}
